import React, { useEffect, useState, useCallback } from "react";
import {
  LayoutDashboard, UserCheck, CreditCard, BookOpen, Users, Bell, Search, Plus,
  CheckCircle2, XCircle, Clock, Send, TrendingUp, Settings,
  ChevronRight, Music, HelpCircle, Download, LogOut, Loader2, MicVocal, IndianRupee, UserCog
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "./components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "./components/ui/dropdown-menu";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import confetti from "canvas-confetti";

import { AuthProvider, useAuth } from "./AuthContext";
import Login from "./Login";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import AdminTeachers from "./AdminTeachers";
import api from "./api";

// ---------------------------------------------------------------------------
// Constants — music-focused
// ---------------------------------------------------------------------------
const INSTRUMENT_OPTIONS = [
  "Piano", "Guitar", "Violin", "Drums", "Vocals", "Flute", "Cello", "Saxophone", "Ukulele", "Keyboard"
];
const LEVEL_OPTIONS = ["Beginner", "Intermediate", "Advanced"];
const FEE_CYCLE_OPTIONS = ["Monthly", "Quarterly", "Annual"];

const fmtMoney = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY_STUDENTS_MSG = "No students added yet. Click Add Student to begin.";

// ---------------------------------------------------------------------------
// Portal (main authenticated app)
// ---------------------------------------------------------------------------
function Portal() {
  const { user, logout } = useAuth();

  // Data state
  const [students, setStudents] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [instrumentDist, setInstrumentDist] = useState([]);

  // UI state
  const [activeTab, setActiveTab] = useState("dashboard");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInstrumentFilter, setSelectedInstrumentFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  // Attendance
  const [attendanceDate, setAttendanceDate] = useState(todayISO());
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [savingAttendance, setSavingAttendance] = useState(false);

  // Dialog state
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [isAddAssignmentOpen, setIsAddAssignmentOpen] = useState(false);
  const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);

  // Forms
  const [newStudent, setNewStudent] = useState({
    name: "",
    instrument: "Piano",
    level: "Beginner",
    teacher: "",
    class_schedule: "",
    fee_cycle: "Monthly",
    class_count: 4,
    email: "",
    phone: "",
    dues: 0,
  });
  const [newAssignment, setNewAssignment] = useState({
    title: "", instrument: "Piano", due_date: "", total_students: 0
  });
  const [broadcastMsg, setBroadcastMsg] = useState({
    target: "All Students", title: "", message: "", channel: "WhatsApp & Email"
  });

  // -------------------------------------------------------------------------
  // Data loaders
  // -------------------------------------------------------------------------
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, i, a, st, rev, dist] = await Promise.all([
        api.get("/students"),
        api.get("/invoices"),
        api.get("/assignments"),
        api.get("/stats/dashboard"),
        api.get("/stats/revenue"),
        api.get("/stats/instruments"),
      ]);
      setStudents(s.data);
      setInvoices(i.data);
      setAssignments(a.data);
      setStats(st.data);
      setRevenueData(rev.data);
      setInstrumentDist(dist.data);
    } catch (err) {
      toast.error("Failed to load data. Check backend connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAttendance = useCallback(async (date) => {
    try {
      const { data } = await api.get(`/attendance?date=${date}`);
      setAttendanceRecords(data);
    } catch {
      setAttendanceRecords({});
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadAttendance(attendanceDate); }, [attendanceDate, loadAttendance]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------
  const handleSaveAttendance = async () => {
    if (students.length === 0) return;
    setSavingAttendance(true);
    try {
      const records = students.map((s) => ({
        student_id: s.id,
        status: attendanceRecords[s.id] || "present",
      }));
      await api.post("/attendance/save", { date: attendanceDate, records });
      confetti({ particleCount: 50, spread: 60, origin: { y: 0.6 } });
      toast.success(`Attendance for ${attendanceDate} saved!`);
      loadAll();
    } catch {
      toast.error("Could not save attendance.");
    } finally {
      setSavingAttendance(false);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!newStudent.name.trim()) return;
    try {
      const payload = {
        ...newStudent,
        dues: parseFloat(newStudent.dues) || 0,
        class_count: parseInt(newStudent.class_count) || 0,
        email: newStudent.email || null,
        phone: newStudent.phone || null,
        teacher: newStudent.teacher || null,
        class_schedule: newStudent.class_schedule || null,
      };
      const { data } = await api.post("/students", payload);
      setStudents([data, ...students]);
      if ((parseFloat(newStudent.dues) || 0) > 0) {
        const inv = await api.post("/invoices", {
          student_id: data.id,
          amount: parseFloat(newStudent.dues),
          due_date: new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10),
        });
        setInvoices([inv.data, ...invoices]);
      }
      setIsAddStudentOpen(false);
      setNewStudent({
        name: "", instrument: "Piano", level: "Beginner", teacher: "",
        class_schedule: "", fee_cycle: "Monthly", class_count: 4,
        email: "", phone: "", dues: 0,
      });
      toast.success(`Enrolled ${data.name} for ${data.instrument}!`);
      loadAll();
    } catch (err) {
      toast.error("Could not enroll student.");
    }
  };

  const handleAddAssignment = async (e) => {
    e.preventDefault();
    if (!newAssignment.title.trim()) return;
    try {
      const { data } = await api.post("/assignments", {
        ...newAssignment,
        total_students: parseInt(newAssignment.total_students) || 0,
        due_date: newAssignment.due_date || new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
      });
      setAssignments([data, ...assignments]);
      setIsAddAssignmentOpen(false);
      setNewAssignment({ title: "", instrument: "Piano", due_date: "", total_students: 0 });
      toast.success("Practice assignment published!");
    } catch {
      toast.error("Could not create assignment.");
    }
  };

  const handleSendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastMsg.title || !broadcastMsg.message) return;
    try {
      await api.post("/reminders/send", {
        kind: "broadcast",
        target: broadcastMsg.target,
        title: broadcastMsg.title,
        message: broadcastMsg.message,
        channel: broadcastMsg.channel,
      });
      setIsBroadcastOpen(false);
      confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 } });
      toast.success(`Broadcast dispatched via ${broadcastMsg.channel} to ${broadcastMsg.target}!`);
      setBroadcastMsg({ target: "All Students", title: "", message: "", channel: "WhatsApp & Email" });
      loadAll();
    } catch {
      toast.error("Broadcast failed.");
    }
  };

  const handleSendReminder = async (invoice) => {
    try {
      await api.post("/reminders/send", {
        kind: "fee",
        target: invoice.student_id,
        title: `Fee due: ${fmtMoney(invoice.amount)}`,
        message: `Dear parent, this is a reminder that invoice ${invoice.id} for ${invoice.student_name} is due on ${invoice.due_date}.`,
        channel: "WhatsApp & Email",
      });
      toast.success(`Fee reminder sent to ${invoice.student_name}!`);
    } catch {
      toast.error("Could not send reminder.");
    }
  };

  const handleMarkPaid = async (invoice) => {
    try {
      const { data } = await api.patch(`/invoices/${invoice.id}/pay`);
      setInvoices(invoices.map((i) => (i.id === invoice.id ? data : i)));
      toast.success(`Marked ${invoice.id} as paid.`);
      loadAll();
    } catch {
      toast.error("Could not mark invoice paid.");
    }
  };

  const handleNotifyAbsence = async (student) => {
    try {
      await api.post("/reminders/send", {
        kind: "attendance",
        target: student.id,
        title: `Absence: ${student.name}`,
        message: `${student.name} was marked absent for ${attendanceDate}.`,
        channel: "SMS & Email",
      });
      toast.success(`Absence notification sent for ${student.name}.`);
    } catch {
      toast.error("Notification failed.");
    }
  };

  const handlePingPractice = async (asg) => {
    try {
      await api.post("/reminders/send", {
        kind: "practice",
        target: "all",
        title: `Practice reminder: ${asg.title}`,
        message: `Reminder to submit "${asg.title}" by ${asg.due_date}.`,
        channel: "WhatsApp",
      });
      toast.success(`Practice reminder sent for "${asg.title}"!`);
    } catch {
      toast.error("Could not send practice reminder.");
    }
  };

  const handleSendBulkFeeReminders = async () => {
    const pending = invoices.filter((i) => i.status !== "Paid");
    if (pending.length === 0) {
      toast.info("No pending invoices to remind.");
      return;
    }
    try {
      await Promise.all(pending.map((inv) => handleSendReminder(inv)));
      confetti({ particleCount: 50, spread: 70, origin: { y: 0.6 } });
    } catch {
      toast.error("Bulk send failed for some reminders.");
    }
  };

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------
  const filteredStudents = students.filter((s) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      s.name.toLowerCase().includes(q) ||
      (s.instrument || "").toLowerCase().includes(q) ||
      (s.teacher || "").toLowerCase().includes(q);
    const matchesInstrument =
      selectedInstrumentFilter === "All" || s.instrument === selectedInstrumentFilter;
    return matchesSearch && matchesInstrument;
  });

  const presentToday = students
    .map((s) => attendanceRecords[s.id] || "present")
    .filter((st) => st === "present").length;
  const absentToday = students
    .map((s) => attendanceRecords[s.id] || "present")
    .filter((st) => st === "absent").length;
  const lateToday = students
    .map((s) => attendanceRecords[s.id] || "present")
    .filter((st) => st === "late").length;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
          <span className="text-sm font-medium">Loading studio data…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] flex flex-col font-sans selection:bg-indigo-500 selection:text-white">
      <Toaster position="top-right" richColors />

      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between shadow-xs" data-testid="app-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-500 flex items-center justify-center text-white shadow-md shadow-indigo-200">
            <Music className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-slate-900 leading-none" data-testid="app-brand">
              Melody Teacher
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 ml-2 capitalize" data-testid="app-role-pill">
                {user?.role || "user"}
              </span>
            </h1>
            <p className="text-xs text-slate-500 mt-1">Music Class Manager · Attendance, Fees & Practice</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative hidden md:block w-72">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search students, instruments…"
              className="pl-9 bg-slate-50 border-slate-200 focus-visible:ring-indigo-500 text-sm h-9 rounded-lg"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="global-search-input"
            />
          </div>

          <Button
            variant="outline" size="sm"
            className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50 gap-2 font-medium"
            onClick={() => setIsBroadcastOpen(true)}
            data-testid="broadcast-header-btn"
          >
            <Bell className="w-4 h-4 text-indigo-600" />
            <span className="hidden sm:inline">Broadcast Notice</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0" data-testid="user-profile-menu">
                <Avatar className="h-9 w-9 border border-indigo-200 shadow-xs">
                  <AvatarFallback className="bg-indigo-600 text-white text-xs font-bold">
                    {(user?.name || "MT").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 mt-2 rounded-xl border-slate-200 shadow-xl">
              <div className="p-3 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100" data-testid="dropdown-role-pill">
                  {user?.role || "user"}
                </span>
              </div>
              <DropdownMenuItem className="cursor-pointer gap-2 py-2 text-xs font-medium text-slate-700">
                <Settings className="w-4 h-4 text-slate-500" /> Studio Settings
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer gap-2 py-2 text-xs font-medium text-slate-700">
                <HelpCircle className="w-4 h-4 text-slate-500" /> Help & Support
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer gap-2 py-2 text-xs font-medium text-rose-600 focus:text-rose-700"
                onClick={logout}
                data-testid="logout-menu-item"
              >
                <LogOut className="w-4 h-4" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* LAYOUT */}
      <div className="flex-1 flex flex-col md:flex-row">
        {/* SIDEBAR */}
        <aside className="w-full md:w-64 bg-white border-r border-[#E2E8F0] p-4 flex flex-col justify-between shrink-0" data-testid="sidebar-nav">
          <div className="space-y-6">
            <div className="px-3 py-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Main Menu</p>
              <nav className="space-y-1">
                {[
                  { id: "dashboard", label: "Dashboard Overview", icon: LayoutDashboard, roles: ["admin", "teacher"] },
                  { id: "attendance", label: "Attendance & Check-In", icon: UserCheck, roles: ["admin", "teacher"] },
                  { id: "fees", label: "Fee Management", icon: CreditCard, roles: ["admin", "teacher"] },
                  { id: "practice", label: "Practice & Homework", icon: BookOpen, roles: ["admin", "teacher"] },
                  { id: "students", label: "Student Profiles", icon: Users, roles: ["admin", "teacher"] },
                  { id: "teachers", label: "Teacher Accounts", icon: UserCog, roles: ["admin"] },
                ].filter(({ roles }) => roles.includes(user?.role)).map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      activeTab === id
                        ? "bg-indigo-600 text-white shadow-md shadow-indigo-200 font-semibold"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                    data-testid={`nav-tab-${id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="px-3 py-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Quick Actions</p>
              <div className="space-y-2">
                <Button className="w-full justify-start gap-2 bg-slate-50 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 border border-slate-200 font-medium text-xs h-9"
                  onClick={() => setIsAddStudentOpen(true)} data-testid="quick-action-add-student">
                  <Plus className="w-3.5 h-3.5 text-indigo-600" /> Add Student
                </Button>
                <Button className="w-full justify-start gap-2 bg-slate-50 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 border border-slate-200 font-medium text-xs h-9"
                  onClick={() => setIsAddAssignmentOpen(true)} data-testid="quick-action-add-assignment">
                  <BookOpen className="w-3.5 h-3.5 text-indigo-600" /> Assign Practice
                </Button>
                <Button className="w-full justify-start gap-2 bg-slate-50 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 border border-slate-200 font-medium text-xs h-9"
                  onClick={() => setIsBroadcastOpen(true)} data-testid="quick-action-broadcast">
                  <Send className="w-3.5 h-3.5 text-indigo-600" /> Send Reminders
                </Button>
              </div>
            </div>
          </div>

          <div className="p-3 bg-indigo-50/60 rounded-2xl border border-indigo-100 mt-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold text-xs">MT</div>
              <div>
                <p className="text-xs font-bold text-slate-900">Studio Live</p>
                <p className="text-[10px] text-slate-500">All systems operational</p>
              </div>
            </div>
          </div>
        </aside>

        {/* CONTENT */}
        <main className="flex-1 p-6 md:p-8 overflow-y-auto">

          {/* DASHBOARD */}
          {activeTab === "dashboard" && (
            <div className="space-y-8" data-testid="dashboard-view">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-900 via-indigo-800 to-violet-900 text-white p-8 shadow-xl">
                <div className="relative z-10 max-w-2xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-indigo-200 text-xs font-semibold backdrop-blur-md mb-4 border border-white/10">
                    <Music className="w-3.5 h-3.5 text-yellow-400" /> Welcome, {user?.name?.split(" ")[0] || "Teacher"}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2">Today's Studio Overview</h2>
                  <p className="text-indigo-200 text-sm leading-relaxed mb-6">
                    {stats?.total_students === 0
                      ? "Your studio is a blank score. Add your first student to start tracking attendance, fees and practice."
                      : <>You have <span className="text-white font-bold">{stats?.assignments?.active || 0} practice sets</span> and <span className="text-white font-bold">{stats?.attendance?.percentage || 0}% attendance</span> today.</>}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => setIsAddStudentOpen(true)} className="bg-white text-indigo-900 hover:bg-indigo-50 font-bold text-xs h-10 px-5 shadow-md gap-2" data-testid="hero-add-student-btn">
                      <Plus className="w-4 h-4" /> Add Student
                    </Button>
                    {stats?.total_students > 0 && (
                      <Button onClick={() => setActiveTab("attendance")} variant="outline" className="border-white/30 text-white hover:bg-white/10 font-medium text-xs h-10 px-5">
                        Take Attendance Now
                      </Button>
                    )}
                  </div>
                </div>
                <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none transform translate-x-10 translate-y-10">
                  <MicVocal className="w-96 h-96" />
                </div>
              </div>

              {/* STATS GRID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard label="Total Students" value={`${stats?.total_students || 0}`} icon={Users} iconBg="bg-indigo-50" iconColor="text-indigo-600"
                  note={stats?.total_students > 0 ? <><TrendingUp className="w-3 h-3" /> Active roster</> : "None yet"} noteColor="text-slate-500" testId="stat-students" />
                <StatCard label="Today's Attendance" value={`${stats?.attendance?.percentage || 0}%`} icon={UserCheck} iconBg="bg-emerald-50" iconColor="text-emerald-600"
                  note={`${stats?.attendance?.present || 0} present · ${stats?.attendance?.absent || 0} absent`} noteColor="text-emerald-600" testId="stat-attendance" />
                <StatCard label="Fees Collected" value={fmtMoney(stats?.fees?.collected || 0)} icon={IndianRupee} iconBg="bg-sky-50" iconColor="text-sky-600"
                  note={`${fmtMoney(stats?.fees?.pending || 0)} pending`} noteColor="text-slate-500" testId="stat-fees" />
                <StatCard label="Practice Reminders" value={`${stats?.assignments?.active || 0}`} icon={Clock} iconBg="bg-amber-50" iconColor="text-amber-600"
                  note={`${stats?.assignments?.total || 0} total assignments`} noteColor="text-amber-600" testId="stat-reminders" />
              </div>

              {/* CHARTS */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-2 border border-slate-200/80 shadow-xs bg-white rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-base font-bold text-slate-900">Revenue & Fee Collection</h3>
                      <p className="text-xs text-slate-500">Monthly tuition billed vs. collected</p>
                    </div>
                    <Badge variant="outline" className="border-indigo-200 text-indigo-600 bg-indigo-50/50">Live</Badge>
                  </div>
                  <div className="h-72 w-full relative">
                    {revenueData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorCollected" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                          <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} />
                          <YAxis stroke="#64748B" fontSize={12} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                          <Tooltip contentStyle={{ backgroundColor: "#0F172A", color: "#FFF", borderRadius: "12px", border: "none" }} />
                          <Area type="monotone" dataKey="collected" stroke="#4F46E5" strokeWidth={3} fillOpacity={1} fill="url(#colorCollected)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <EmptyChart label="Revenue chart will appear here once fees are recorded." />
                    )}
                  </div>
                </Card>

                <Card className="border border-slate-200/80 shadow-xs bg-white rounded-2xl p-6 flex flex-col justify-between">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">Instrument Breakdown</h3>
                    <p className="text-xs text-slate-500 mb-6">Students by instrument</p>
                    {instrumentDist.length > 0 ? (
                      <div className="space-y-4">
                        {instrumentDist.map((item, idx) => {
                          const colors = ["#4F46E5", "#0EA5E9", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"];
                          return (
                            <div key={idx} className="space-y-1.5">
                              <div className="flex justify-between text-xs font-semibold">
                                <span className="text-slate-700">{item.name}</span>
                                <span className="text-slate-900">{item.count} ({item.percentage}%)</span>
                              </div>
                              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${item.percentage}%`, backgroundColor: colors[idx % colors.length] }}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 py-8 text-center" data-testid="empty-instruments">No instruments to chart yet.</p>
                    )}
                  </div>
                  <div className="pt-6 border-t border-slate-100 mt-6">
                    <Button onClick={() => setActiveTab("students")} variant="outline" className="w-full text-xs font-semibold border-slate-200 hover:bg-slate-50">
                      View All Students <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </Card>
              </div>

              {/* RECENT ACTIVITY */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border border-slate-200/80 shadow-xs bg-white rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-slate-900">Fee Reminders Due</h3>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab("fees")} className="text-indigo-600 text-xs font-semibold hover:bg-indigo-50">View All</Button>
                  </div>
                  <div className="space-y-3">
                    {invoices.filter((i) => i.status !== "Paid").slice(0, 3).map((inv, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <div>
                          <p className="text-sm font-bold text-slate-900">{inv.student_name}</p>
                          <p className="text-xs text-slate-500">{inv.instrument} · Due {inv.due_date}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-extrabold text-slate-900">{fmtMoney(inv.amount)}</span>
                          <Button size="sm" onClick={() => handleSendReminder(inv)} className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-3">Remind</Button>
                        </div>
                      </div>
                    ))}
                    {invoices.filter((i) => i.status !== "Paid").length === 0 && (
                      <p className="text-xs text-slate-400 py-4 text-center" data-testid="empty-fees-dash">No pending fees yet.</p>
                    )}
                  </div>
                </Card>

                <Card className="border border-slate-200/80 shadow-xs bg-white rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-slate-900">Active Practice Sets</h3>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab("practice")} className="text-indigo-600 text-xs font-semibold hover:bg-indigo-50">View All</Button>
                  </div>
                  <div className="space-y-3">
                    {assignments.slice(0, 3).map((asg, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <div>
                          <p className="text-sm font-bold text-slate-900">{asg.title}</p>
                          <p className="text-xs text-slate-500">{asg.instrument} · {asg.submitted_count}/{asg.total_students} submitted</p>
                        </div>
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium text-xs">{asg.status}</Badge>
                      </div>
                    ))}
                    {assignments.length === 0 && <p className="text-xs text-slate-400 py-4 text-center" data-testid="empty-practice-dash">No practice reminders yet.</p>}
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* ATTENDANCE */}
          {activeTab === "attendance" && (
            <div className="space-y-6" data-testid="attendance-view">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Attendance Tracker</h2>
                  <p className="text-xs text-slate-500 mt-1">Record daily attendance and send parent alerts.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    type="date" value={attendanceDate}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    className="w-auto h-10 border-slate-200 bg-white text-sm font-medium"
                    data-testid="attendance-date-picker"
                  />
                  <Button
                    onClick={handleSaveAttendance}
                    disabled={savingAttendance || students.length === 0}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold h-10 px-4"
                    data-testid="save-attendance-btn"
                  >
                    {savingAttendance ? "Saving…" : "Save Attendance"}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <MetricPill icon={CheckCircle2} iconBg="bg-emerald-50" iconColor="text-emerald-600" label="Present Today" value={`${presentToday}`} />
                <MetricPill icon={XCircle} iconBg="bg-rose-50" iconColor="text-rose-600" label="Absent Today" value={`${absentToday}`} />
                <MetricPill icon={Clock} iconBg="bg-amber-50" iconColor="text-amber-600" label="Late Arrival" value={`${lateToday}`} />
              </div>

              <Card className="border border-slate-200 shadow-xs bg-white rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-base font-bold text-slate-900">Daily Roster · {attendanceDate}</h3>
                  {students.length > 0 && (
                    <Button
                      variant="outline" size="sm"
                      className="text-xs font-medium border-slate-200"
                      onClick={() => {
                        const updated = {};
                        students.forEach((s) => (updated[s.id] = "present"));
                        setAttendanceRecords(updated);
                        toast.success("Marked all students present!");
                      }}
                      data-testid="mark-all-present-btn"
                    >
                      Mark All Present
                    </Button>
                  )}
                </div>
                {students.length === 0 ? (
                  <EmptyState msg={EMPTY_STUDENTS_MSG} onAdd={() => setIsAddStudentOpen(true)} testId="empty-attendance" />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/70 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                          <th className="py-3 px-6">Student</th>
                          <th className="py-3 px-6">Instrument · Level</th>
                          <th className="py-3 px-6 text-center">Status</th>
                          <th className="py-3 px-6 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {students.map((student) => {
                          const status = attendanceRecords[student.id] || "present";
                          return (
                            <tr key={student.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`attendance-row-${student.id}`}>
                              <td className="py-4 px-6 font-semibold text-slate-900 flex items-center gap-3">
                                <Avatar className="h-9 w-9 border border-slate-200">
                                  <AvatarFallback className="bg-indigo-100 text-indigo-700 font-bold text-xs">
                                    {student.name.slice(0, 2).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-semibold text-slate-900">{student.name}</p>
                                  <p className="text-xs text-slate-400">{student.id}</p>
                                </div>
                              </td>
                              <td className="py-4 px-6 text-slate-600 font-medium text-xs">{student.instrument} · {student.level}</td>
                              <td className="py-4 px-6 text-center">
                                <div className="inline-flex rounded-lg p-1 bg-slate-100 border border-slate-200 gap-1">
                                  {["present", "late", "absent"].map((s) => (
                                    <button
                                      key={s} type="button"
                                      onClick={() => setAttendanceRecords({ ...attendanceRecords, [student.id]: s })}
                                      className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                                        status === s
                                          ? s === "present" ? "bg-emerald-600 text-white shadow-xs"
                                            : s === "late" ? "bg-amber-500 text-white shadow-xs"
                                            : "bg-rose-600 text-white shadow-xs"
                                          : "text-slate-600 hover:text-slate-900"
                                      }`}
                                      data-testid={`status-${s}-${student.id}`}
                                    >
                                      {s[0].toUpperCase() + s.slice(1)}
                                    </button>
                                  ))}
                                </div>
                              </td>
                              <td className="py-4 px-6 text-right">
                                {status === "absent" && (
                                  <Button size="sm" variant="outline"
                                    className="h-8 border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-medium gap-1"
                                    onClick={() => handleNotifyAbsence(student)}
                                    data-testid={`notify-parent-${student.id}`}>
                                    <Send className="w-3 h-3" /> Notify Parent
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* FEES */}
          {activeTab === "fees" && (
            <div className="space-y-6" data-testid="fees-view">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Fee Management</h2>
                  <p className="text-xs text-slate-500 mt-1">Track tuition invoices, record payments, dispatch reminders.</p>
                </div>
                <Button
                  onClick={handleSendBulkFeeReminders}
                  disabled={invoices.filter((i) => i.status !== "Paid").length === 0}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold h-10 px-4 gap-2"
                  data-testid="send-bulk-reminders-btn"
                >
                  <Send className="w-4 h-4" /> Send All Pending Reminders
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FeeStatCard label="Total Billed" value={fmtMoney(stats?.fees?.total_billed || 0)}
                  sub={`${stats?.fees && stats.fees.total_billed > 0 ? Math.round(100 * stats.fees.collected / stats.fees.total_billed) : 0}% collection rate`} subColor="text-emerald-600" />
                <FeeStatCard label="Pending Dues" value={fmtMoney(stats?.fees?.pending || 0)} valueColor="text-amber-600"
                  sub={`${invoices.filter((i) => i.status === "Pending").length} invoices pending`} />
                <FeeStatCard label="Overdue Accounts" value={`${stats?.fees?.overdue_count || 0}`} valueColor="text-rose-600"
                  sub={`${stats?.fees?.overdue_count ? "Action required" : "None outstanding"}`} subColor="text-rose-500" />
              </div>

              <Card className="border border-slate-200 shadow-xs bg-white rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-base font-bold text-slate-900">Active Tuition Invoices</h3>
                  <Badge variant="outline" className="border-indigo-200 text-indigo-600 bg-indigo-50">Live</Badge>
                </div>
                {invoices.length === 0 ? (
                  <EmptyState msg={EMPTY_STUDENTS_MSG} onAdd={() => setIsAddStudentOpen(true)} testId="empty-fees" />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/70 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                          <th className="py-3 px-6">Invoice ID</th>
                          <th className="py-3 px-6">Student</th>
                          <th className="py-3 px-6">Instrument</th>
                          <th className="py-3 px-6">Amount</th>
                          <th className="py-3 px-6">Due Date</th>
                          <th className="py-3 px-6">Status</th>
                          <th className="py-3 px-6 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {invoices.map((inv) => (
                          <tr key={inv.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                            <td className="py-4 px-6 font-mono text-xs font-semibold text-slate-500">{inv.id}</td>
                            <td className="py-4 px-6 font-bold text-slate-900">{inv.student_name}</td>
                            <td className="py-4 px-6 text-xs text-slate-600 font-medium">{inv.instrument}</td>
                            <td className="py-4 px-6 font-extrabold text-slate-900">{fmtMoney(inv.amount)}</td>
                            <td className="py-4 px-6 text-xs text-slate-500">{inv.due_date}</td>
                            <td className="py-4 px-6"><StatusPill status={inv.status} /></td>
                            <td className="py-4 px-6 text-right space-x-2">
                              {inv.status !== "Paid" ? (
                                <>
                                  <Button size="sm" onClick={() => handleSendReminder(inv)}
                                    className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-3 gap-1.5"
                                    data-testid={`reminder-btn-${inv.id}`}>
                                    <Send className="w-3 h-3" /> Remind
                                  </Button>
                                  <Button size="sm" variant="outline"
                                    onClick={() => handleMarkPaid(inv)}
                                    className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs font-medium"
                                    data-testid={`markpaid-btn-${inv.id}`}>
                                    Mark Paid
                                  </Button>
                                </>
                              ) : (
                                <Button size="sm" variant="outline"
                                  onClick={() => toast.success(`Receipt downloaded for ${inv.student_name}`)}
                                  className="h-8 border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-medium gap-1">
                                  <Download className="w-3 h-3" /> Receipt
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* PRACTICE */}
          {activeTab === "practice" && (
            <div className="space-y-6" data-testid="practice-view">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Practice & Homework</h2>
                  <p className="text-xs text-slate-500 mt-1">Assign practice sets, monitor progress, send motivation reminders.</p>
                </div>
                <Button
                  onClick={() => setIsAddAssignmentOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold h-10 px-4 gap-2"
                  data-testid="add-homework-btn"
                >
                  <Plus className="w-4 h-4" /> Assign Practice
                </Button>
              </div>

              <Card className="border border-slate-200 shadow-xs bg-white rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-base font-bold text-slate-900">Current Practice Sets</h3>
                  <Badge variant="outline" className="border-indigo-200 text-indigo-600 bg-indigo-50">Live</Badge>
                </div>
                {assignments.length === 0 ? (
                  <div className="p-10 text-center" data-testid="empty-practice">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 mx-auto flex items-center justify-center mb-3">
                      <BookOpen className="w-6 h-6" />
                    </div>
                    <p className="text-sm font-semibold text-slate-900">No practice reminders yet.</p>
                    <p className="text-xs text-slate-500 mt-1">Click Assign Practice to publish your first set.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/70 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                          <th className="py-3 px-6">Title</th>
                          <th className="py-3 px-6">Instrument</th>
                          <th className="py-3 px-6">Due Date</th>
                          <th className="py-3 px-6">Submissions</th>
                          <th className="py-3 px-6">Status</th>
                          <th className="py-3 px-6 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {assignments.map((asg) => (
                          <tr key={asg.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`assignment-row-${asg.id}`}>
                            <td className="py-4 px-6">
                              <p className="font-bold text-slate-900">{asg.title}</p>
                              <p className="text-xs text-slate-400 font-mono">{asg.id}</p>
                            </td>
                            <td className="py-4 px-6 text-xs text-slate-600 font-medium">{asg.instrument}</td>
                            <td className="py-4 px-6 text-xs text-slate-500">{asg.due_date}</td>
                            <td className="py-4 px-6 font-semibold text-slate-900">{asg.submitted_count}/{asg.total_students}</td>
                            <td className="py-4 px-6">
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">{asg.status}</span>
                            </td>
                            <td className="py-4 px-6 text-right">
                              <Button size="sm" onClick={() => handlePingPractice(asg)}
                                className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-3 gap-1"
                                data-testid={`ping-practice-${asg.id}`}>
                                <Send className="w-3 h-3" /> Ping Reminders
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* STUDENTS */}
          {activeTab === "students" && (
            <div className="space-y-6" data-testid="students-view">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Student Profiles</h2>
                  <p className="text-xs text-slate-500 mt-1">Manage enrolments, instruments, fees and schedules.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedInstrumentFilter} onValueChange={setSelectedInstrumentFilter}>
                    <SelectTrigger className="w-44 h-10 bg-white border-slate-200 text-xs font-medium" data-testid="instrument-filter-select">
                      <SelectValue placeholder="Filter by Instrument" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All">All Instruments</SelectItem>
                      {INSTRUMENT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <Button
                    onClick={() => setIsAddStudentOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold h-10 px-4 gap-2"
                    data-testid="add-student-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Student
                  </Button>
                </div>
              </div>

              <Card className="border border-slate-200 shadow-xs bg-white rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-base font-bold text-slate-900">Enrolled Students ({filteredStudents.length})</h3>
                  <span className="text-xs text-slate-500">Live from backend</span>
                </div>
                {students.length === 0 ? (
                  <EmptyState msg={EMPTY_STUDENTS_MSG} onAdd={() => setIsAddStudentOpen(true)} testId="empty-students" />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/70 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                          <th className="py-3 px-6">Student</th>
                          <th className="py-3 px-6">Instrument · Level</th>
                          <th className="py-3 px-6">Teacher</th>
                          <th className="py-3 px-6">Schedule</th>
                          <th className="py-3 px-6">Fee Cycle</th>
                          <th className="py-3 px-6">Attendance</th>
                          <th className="py-3 px-6">Dues</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {filteredStudents.map((s) => (
                          <tr key={s.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`student-row-${s.id}`}>
                            <td className="py-4 px-6 flex items-center gap-3">
                              <Avatar className="h-10 w-10 border border-slate-200">
                                <AvatarFallback className="bg-indigo-100 text-indigo-700 font-bold text-xs">
                                  {s.name.slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-bold text-slate-900">{s.name}</p>
                                <p className="text-xs text-slate-400">{s.email || "—"} · {s.phone || "—"}</p>
                              </div>
                            </td>
                            <td className="py-4 px-6 text-xs text-slate-700 font-medium">{s.instrument} · <span className="text-slate-500">{s.level}</span></td>
                            <td className="py-4 px-6 text-xs text-slate-600">{s.teacher || "—"}</td>
                            <td className="py-4 px-6 text-xs text-slate-600">{s.class_schedule || "—"}</td>
                            <td className="py-4 px-6 text-xs text-slate-600">{s.fee_cycle} · {s.class_count} classes</td>
                            <td className="py-4 px-6 font-bold text-emerald-600">{s.attendance_rate}%</td>
                            <td className="py-4 px-6 font-extrabold text-slate-900">{fmtMoney(s.dues)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}
          {/* TEACHERS (admin only) */}
          {activeTab === "teachers" && user?.role === "admin" && (
            <AdminTeachers />
          )}

        </main>
      </div>

      {/* MODAL: ADD STUDENT */}
      <Dialog open={isAddStudentOpen} onOpenChange={setIsAddStudentOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl border-slate-200 shadow-2xl p-6 bg-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">Add Student</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStudent} className="space-y-4 mt-2">
            <Field label="Student Full Name">
              <Input placeholder="e.g. Maya Kapoor" value={newStudent.name}
                onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                required className="border-slate-200 text-sm h-10" data-testid="new-student-name" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Instrument">
                <Select value={newStudent.instrument} onValueChange={(v) => setNewStudent({ ...newStudent, instrument: v })}>
                  <SelectTrigger className="border-slate-200 text-sm h-10" data-testid="new-student-instrument-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{INSTRUMENT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Level">
                <Select value={newStudent.level} onValueChange={(v) => setNewStudent({ ...newStudent, level: v })}>
                  <SelectTrigger className="border-slate-200 text-sm h-10" data-testid="new-student-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVEL_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Teacher">
                <Input placeholder="e.g. Ms. Clara Wren" value={newStudent.teacher}
                  onChange={(e) => setNewStudent({ ...newStudent, teacher: e.target.value })}
                  className="border-slate-200 text-sm h-10" data-testid="new-student-teacher" />
              </Field>
              <Field label="Class Schedule">
                <Input placeholder="e.g. Mon/Wed 5:00 PM" value={newStudent.class_schedule}
                  onChange={(e) => setNewStudent({ ...newStudent, class_schedule: e.target.value })}
                  className="border-slate-200 text-sm h-10" data-testid="new-student-schedule" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fee Cycle">
                <Select value={newStudent.fee_cycle} onValueChange={(v) => setNewStudent({ ...newStudent, fee_cycle: v })}>
                  <SelectTrigger className="border-slate-200 text-sm h-10" data-testid="new-student-fee-cycle-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FEE_CYCLE_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Classes per Cycle">
                <Input type="number" min="0" value={newStudent.class_count}
                  onChange={(e) => setNewStudent({ ...newStudent, class_count: e.target.value })}
                  className="border-slate-200 text-sm h-10" data-testid="new-student-class-count" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <Input placeholder="parent@example.com" value={newStudent.email}
                  onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                  className="border-slate-200 text-sm h-10" data-testid="new-student-email" />
              </Field>
              <Field label="Phone">
                <Input placeholder="+91 90000 00000" value={newStudent.phone}
                  onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                  className="border-slate-200 text-sm h-10" data-testid="new-student-phone" />
              </Field>
            </div>
            <Field label="Initial Fee Due (₹)">
              <Input type="number" step="1" placeholder="0" value={newStudent.dues}
                onChange={(e) => setNewStudent({ ...newStudent, dues: e.target.value })}
                className="border-slate-200 text-sm h-10" data-testid="new-student-dues" />
            </Field>
            <DialogFooter className="pt-4 border-t border-slate-100 flex gap-2">
              <Button type="button" variant="outline" onClick={() => setIsAddStudentOpen(false)} className="text-xs font-medium">Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-5" data-testid="submit-new-student">Add Student</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL: ASSIGN PRACTICE */}
      <Dialog open={isAddAssignmentOpen} onOpenChange={setIsAddAssignmentOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl border-slate-200 shadow-2xl p-6 bg-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">Assign Practice Set</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAssignment} className="space-y-4 mt-2">
            <Field label="Practice Title">
              <Input placeholder="e.g. Hanon Exercise 12" value={newAssignment.title}
                onChange={(e) => setNewAssignment({ ...newAssignment, title: e.target.value })}
                required className="border-slate-200 text-sm h-10" data-testid="new-assignment-title" />
            </Field>
            <Field label="Instrument">
              <Select value={newAssignment.instrument} onValueChange={(v) => setNewAssignment({ ...newAssignment, instrument: v })}>
                <SelectTrigger className="border-slate-200 text-sm h-10" data-testid="new-assignment-instrument-select"><SelectValue /></SelectTrigger>
                <SelectContent>{INSTRUMENT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Due Date">
                <Input type="date" value={newAssignment.due_date}
                  onChange={(e) => setNewAssignment({ ...newAssignment, due_date: e.target.value })}
                  className="border-slate-200 text-sm h-10" data-testid="new-assignment-due-date" />
              </Field>
              <Field label="Total Students">
                <Input type="number" min="0" value={newAssignment.total_students}
                  onChange={(e) => setNewAssignment({ ...newAssignment, total_students: e.target.value })}
                  className="border-slate-200 text-sm h-10" />
              </Field>
            </div>
            <DialogFooter className="pt-4 border-t border-slate-100 flex gap-2">
              <Button type="button" variant="outline" onClick={() => setIsAddAssignmentOpen(false)} className="text-xs font-medium">Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-5" data-testid="submit-new-assignment">Publish Practice</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL: BROADCAST */}
      <Dialog open={isBroadcastOpen} onOpenChange={setIsBroadcastOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl border-slate-200 shadow-2xl p-6 bg-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">Broadcast Notice & Reminders</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendBroadcast} className="space-y-4 mt-2">
            <Field label="Target Audience">
              <Select value={broadcastMsg.target} onValueChange={(v) => setBroadcastMsg({ ...broadcastMsg, target: v })}>
                <SelectTrigger className="border-slate-200 text-sm h-10" data-testid="broadcast-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Students">All Students</SelectItem>
                  <SelectItem value="Pending Fees Students">Students with Pending Fees</SelectItem>
                  {INSTRUMENT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c} students</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Delivery Channel">
              <Select value={broadcastMsg.channel} onValueChange={(v) => setBroadcastMsg({ ...broadcastMsg, channel: v })}>
                <SelectTrigger className="border-slate-200 text-sm h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="WhatsApp & Email">WhatsApp, SMS & Email</SelectItem>
                  <SelectItem value="WhatsApp Only">WhatsApp Only</SelectItem>
                  <SelectItem value="Email Portal">Email Portal</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Title">
              <Input placeholder="e.g. Recital rescheduled" value={broadcastMsg.title}
                onChange={(e) => setBroadcastMsg({ ...broadcastMsg, title: e.target.value })}
                required className="border-slate-200 text-sm h-10" data-testid="broadcast-title" />
            </Field>
            <Field label="Message">
              <Textarea placeholder="Type your reminder..." value={broadcastMsg.message}
                onChange={(e) => setBroadcastMsg({ ...broadcastMsg, message: e.target.value })}
                required className="border-slate-200 text-sm min-h-24" data-testid="broadcast-message" />
            </Field>
            <DialogFooter className="pt-4 border-t border-slate-100 flex gap-2">
              <Button type="button" variant="outline" onClick={() => setIsBroadcastOpen(false)} className="text-xs font-medium">Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-5" data-testid="submit-broadcast">Dispatch Broadcast</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------
function StatCard({ label, value, icon: Icon, iconBg, iconColor, note, noteColor, testId }) {
  return (
    <Card className="border border-slate-200/80 shadow-xs hover:shadow-md transition-all bg-white rounded-2xl" data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</CardTitle>
        <div className={`w-8 h-8 rounded-xl ${iconBg} flex items-center justify-center ${iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-extrabold text-slate-900">{value}</div>
        <p className={`text-xs font-medium mt-1 flex items-center gap-1 ${noteColor || "text-slate-500"}`}>{note}</p>
      </CardContent>
    </Card>
  );
}

function MetricPill({ icon: Icon, iconBg, iconColor, label, value }) {
  return (
    <div className="p-4 bg-white rounded-2xl border border-slate-200 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl ${iconBg} ${iconColor} flex items-center justify-center font-bold`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="text-xl font-extrabold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function FeeStatCard({ label, value, valueColor = "text-slate-900", sub, subColor = "text-slate-500" }) {
  return (
    <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-xs">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-extrabold ${valueColor} mt-1`}>{value}</p>
      <p className={`text-xs ${subColor} mt-1 font-medium`}>{sub}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Pending: "bg-amber-50 text-amber-700 border-amber-200",
    Upcoming: "bg-sky-50 text-sky-700 border-sky-200",
    Overdue: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${map[status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
      {status}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function EmptyState({ msg, onAdd, testId }) {
  return (
    <div className="p-12 text-center" data-testid={testId}>
      <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 mx-auto flex items-center justify-center mb-4">
        <Users className="w-7 h-7" />
      </div>
      <p className="text-sm font-semibold text-slate-900">{msg}</p>
      <Button
        onClick={onAdd}
        className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold gap-2"
        data-testid="empty-state-add-btn"
      >
        <Plus className="w-4 h-4" /> Add Student
      </Button>
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="h-full w-full flex items-center justify-center border border-dashed border-slate-200 rounded-xl">
      <p className="text-xs text-slate-400 text-center px-6" data-testid="empty-chart">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root — routes public views (login / forgot / reset) or authenticated Portal
// ---------------------------------------------------------------------------
function Shell() {
  const { user, checking } = useAuth();
  const [publicView, setPublicView] = useState(() => {
    // Auto-detect reset link (?token=... in URL)
    const params = new URLSearchParams(window.location.search);
    return params.get("token") ? "reset" : "login";
  });
  const resetToken = new URLSearchParams(window.location.search).get("token") || "";

  if (checking) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (user) return <Portal />;

  if (publicView === "forgot") {
    return <ForgotPassword onBack={() => setPublicView("login")} />;
  }
  if (publicView === "reset") {
    return (
      <ResetPassword
        token={resetToken}
        onDone={() => {
          // Clear ?token= from URL then return to login
          window.history.replaceState({}, "", window.location.pathname);
          setPublicView("login");
        }}
      />
    );
  }
  return <Login onForgot={() => setPublicView("forgot")} />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <Shell />
    </AuthProvider>
  );
}
